// ═══════════════════════════════════════════════════════════════════════════════
// useLinkedInAnalytics Hook
// React Query hook for LinkedIn draft analytics
// ═══════════════════════════════════════════════════════════════════════════════

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface LinkedInAnalytics {
  funnel: {
    generated: number;
    approved: number;
    rejected: number;
    sent: number;
    gotReply: number;
    meetingsBooked: number;
    conversionRates: {
      approvalRate: number;
      sendRate: number;
      responseRate: number;
      meetingRate: number;
    };
  };
  methodologyComparison: Array<{
    methodology: string;
    draftsGenerated: number;
    approvalRate: number;
    responseRate: number;
    avgAiConfidence: number;
    actualSuccessRate: number;
    calibrationError: number;
    avgResponseTimeHours: number;
    meetingsBooked: number;
  }>;
  calibration: {
    byConfidenceBucket: Array<{
      bucket: string;
      avgPredicted: number;
      avgActual: number;
      sampleSize: number;
    }>;
    overallCalibrationScore: number;
  };
  trends: Array<{
    date: string;
    sent: number;
    replies: number;
    responseRate: number;
    byMethodology: Record<string, { sent: number; replies: number }>;
  }>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────

export function useLinkedInAnalytics(
  days = 30,
): ReturnType<typeof useQuery<LinkedInAnalytics>> {
  return useQuery<LinkedInAnalytics>({
    queryKey: ['linkedin-analytics', days],
    queryFn: async (): Promise<LinkedInAnalytics> => {
      // The endpoint authorizes via `Authorization: Bearer <token>`; without it
      // every request failed auth and surfaced as an opaque 500.
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`/api/linkedin-analytics?days=${days}`, {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch LinkedIn analytics');
      }

      return response.json();
    },
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
    staleTime: 2 * 60 * 1000, // Consider data stale after 2 minutes
  });
}
